import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';

const API = 'https://api.schedpilot.com/wp-json';

// Caption fonts — must mirror ASS_FONT_NAMES in jobs/renderVideoOverlays.js.
const FONTS = [
  { key: 'inter',      label: 'Inter',      css: "'Inter', Arial, sans-serif" },
  { key: 'montserrat', label: 'Montserrat', css: "'Montserrat', Arial, sans-serif" },
  { key: 'poppins',    label: 'Poppins',    css: "'Poppins', Arial, sans-serif" },
  { key: 'roboto',     label: 'Roboto',     css: "'Roboto', Arial, sans-serif" },
  { key: 'oswald',     label: 'Oswald',     css: "'Oswald', 'Arial Narrow', Arial, sans-serif" },
  { key: 'anton',      label: 'Anton',      css: "'Anton', Impact, sans-serif" },
  { key: 'bebas',      label: 'Bebas Neue', css: "'Bebas Neue', Impact, sans-serif" },
  { key: 'georgia',    label: 'Georgia',    css: "Georgia, 'Times New Roman', serif" },
  { key: 'courier',    label: 'Courier',    css: "'Courier New', Courier, monospace" },
];
const fontCss = (key) => (FONTS.find(f => f.key === key) || FONTS[0]).css;
const GFONTS_URL = 'https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=Inter:wght@400;700&family=Montserrat:wght@600;800&family=Oswald:wght@500;700&family=Poppins:wght@600;700&family=Roboto:wght@500;700&display=swap';

// Human labels for the render job stages the node worker reports.
const STAGE_LABELS = {
  normalizing:        'Preparing clips',
  concatenating:      'Stitching',
  'extracting-audio': 'Extracting audio',
  transcribing:       'Transcribing',
  'burning-captions': 'Adding captions',
  'awaiting-review':  'Ready for review',
  rendering:          'Rendering',
};

const SPIN_CSS = `
@keyframes sp-spin { to { transform: rotate(360deg); } }
@keyframes sp-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.14); opacity: .7; } }
.sp-spin { animation: sp-spin .9s linear infinite; }
.sp-pulse { animation: sp-pulse 1.6s ease-in-out infinite; }
`;

const defaultCaptions = () => ({
  enabled: true,
  font: 'inter',
  fontSize: 0.06,
  color: '#ffffff',
  bgColor: null,
  bgOpacity: 0.5,
  position: 'bottom',
  outline: true,
  outlineColor: '#000000',
  uppercase: false,
  maxWords: 5,
  review: true, // pause after transcription so the text can be corrected
});

function StitchedVideoEditor() {
  const navigate = useNavigate();

  useEffect(() => {
    if (document.getElementById('sp-stitch-fonts')) return;
    const link = document.createElement('link');
    link.id = 'sp-stitch-fonts';
    link.rel = 'stylesheet';
    link.href = GFONTS_URL;
    document.head.appendChild(link);
  }, []);

  const [mode, setMode] = useState('projects'); // projects | pick | edit

  // Core editor state
  const [bases, setBases]       = useState([]);    // [{ id, file_url, title }]
  const [activeBase, setActiveBase] = useState(0);
  const [stitch, setStitch]     = useState(null);  // { id, file_url, title }
  const [position, setPosition] = useState('append'); // append | prepend
  const [captions, setCaptions] = useState(defaultCaptions());
  const [title, setTitle]       = useState('');

  // Projects
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const projectIdRef = useRef(null);
  const creatingRef  = useRef(false);
  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);

  // Pick screen
  const [pickTarget, setPickTarget] = useState('base'); // base | stitch
  const [tab, setTab]           = useState('upload');    // upload | library
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [convertPct, setConvertPct] = useState(0);
  const [pickBusy, setPickBusy] = useState(false);
  const [library, setLibrary]   = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [pickError, setPickError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [picked, setPicked]     = useState([]); // staged base selection

  // Export
  const [exporting, setExporting]   = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportJobs, setExportJobs] = useState([]);
  const [results, setResults]       = useState([]);
  const exportJobsRef = useRef([]);
  // The last submitted export batch [{ sourceId, jobId, title }] — persisted in
  // the project state so a closed window can come back, check status, and
  // schedule the finished videos.
  const [lastExport, setLastExport] = useState(null);
  // Transcript review editor: { sourceId, jobId, title, segments } while open.
  const [reviewJob, setReviewJob]     = useState(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError]   = useState('');

  const openReview = (j) => {
    setReviewError('');
    setReviewJob({
      sourceId: j.sourceId, jobId: j.jobId, title: j.title,
      segments: (j.segments || []).map(s => ({ ...s })),
    });
  };

  const submitTranscript = async () => {
    if (!reviewJob) return;
    setReviewSaving(true); setReviewError('');
    try {
      const r = await fetch(`${API}/api/v1/update-render-transcript`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: reviewJob.jobId, segments: reviewJob.segments }),
      }).then(x => x.json());
      if (r.code === 200) {
        updateExportJob(reviewJob.sourceId, { status: 'queued', stage: null, segments: null });
        pollOne(reviewJob.sourceId, reviewJob.jobId);
        setReviewJob(null);
      } else {
        setReviewError(r.message || 'Could not save the transcript.');
      }
    } catch {
      setReviewError('Could not save the transcript.');
    }
    setReviewSaving(false);
  };

  // Scheduling (after export)
  const [showSchedule, setShowSchedule] = useState(false);
  const [accounts, setAccounts]         = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [postText, setPostText]         = useState('');
  const [scheduleStart, setScheduleStart] = useState('');
  const [spacingHours, setSpacingHours] = useState(24);
  const [scheduling, setScheduling]     = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleDone, setScheduleDone] = useState(null);
  // Per-video overrides so each post can be a variation: { [videoId]: { title, text } }.
  // Empty text falls back to the shared caption.
  const [variants, setVariants]         = useState({});
  const [accountSearch, setAccountSearch] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');

  const activeBaseVideo = bases[activeBase] || bases[0] || null;

  // ── Uploading (shared by base multi-upload and stitch single-upload) ──
  const handleUpload = async (file, onReady) => {
    if (!file) return;
    setUploading(true); setUploadPct(0); setPickError(''); setPickBusy(true);
    try {
      const form = new FormData();
      form.append('video', file);
      const res = await axios.post(`${API}/custom/v1/upload-video`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      const data = res.data || {};
      if (data.video_id) {
        const t = (file.name || 'video').replace(/\.[^.]+$/, '');
        await waitProcessed(data.video_id, t, onReady);
      } else {
        setPickError(data.message || 'Upload failed.');
      }
    } catch (err) {
      setPickError(err?.response?.data?.message || 'Upload failed. The file may be too large.');
    } finally {
      setUploading(false); setPickBusy(false);
    }
  };

  const handleUploadFiles = async (files, onReady) => {
    const list = Array.from(files || []).filter(f => f.type.startsWith('video/'));
    if (!list.length) { setPickError('Please choose video files.'); return; }
    // Stitch picker only takes one file.
    const toDo = pickTarget === 'stitch' ? list.slice(0, 1) : list;
    for (const f of toDo) { await handleUpload(f, onReady); }
  };

  const waitProcessed = (videoId, fallbackTitle, onReady) => new Promise((resolve) => {
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const r = await fetch(`${API}/api/v1/video-status?id=${videoId}`, { credentials: 'include' }).then(x => x.json());
        if (r.code === 200 && typeof r.progress === 'number') setConvertPct(r.progress);
        if (r.code === 200 && r.status === 'processed') {
          onReady({ id: r.id, file_url: r.file_url, title: r.title || fallbackTitle });
          resolve(); return;
        }
        if (r.code === 200 && r.status === 'failed') { setPickError('A video could not be processed.'); resolve(); return; }
      } catch { /* keep polling */ }
      if (tries < 240) setTimeout(tick, 2500);
      else { setPickError('Conversion timed out.'); resolve(); }
    };
    tick();
  });

  const onUploadReady = (v) => {
    if (pickTarget === 'stitch') { setStitch(v); setMode('edit'); }
    else setPicked(prev => prev.find(p => p.id === v.id) ? prev : [...prev, v]);
  };

  const onDropVideo = (e) => {
    e.preventDefault(); setDragOver(false);
    if (uploading) return;
    handleUploadFiles(e.dataTransfer.files, onUploadReady);
  };

  // Library pagination: the API returns 12 videos per page + a hasMore flag.
  // libPagesRef tracks how many pages are on screen so the silent status
  // refresh re-fetches ALL of them (not just page 1).
  const [libHasMore, setLibHasMore] = useState(false);
  const [libLoadingMore, setLibLoadingMore] = useState(false);
  const libPagesRef = useRef(1);

  const fetchLibPage = async (page) => {
    const r = await fetch(`${API}/api/v1/getMediaLibrary?type=videos&page=${page}`, { credentials: 'include' }).then(x => x.json());
    return (r.code === 200 && Array.isArray(r.data)) ? { items: r.data, hasMore: !!r.hasMore } : { items: [], hasMore: false };
  };

  const loadLibrary = useCallback(async (silent = false) => {
    if (!silent) setLibLoading(true);
    try {
      const all = [];
      let hasMore = false;
      for (let p = 1; p <= libPagesRef.current; p++) {
        const r = await fetchLibPage(p);
        all.push(...r.items.filter(v => !all.find(x => x.id === v.id)));
        hasMore = r.hasMore;
        if (!r.items.length) break;
      }
      setLibrary(all);
      setLibHasMore(hasMore);
    } catch { if (!silent) setLibrary([]); }
    if (!silent) setLibLoading(false);
  }, []);

  const loadMoreLibrary = async () => {
    if (libLoadingMore) return;
    setLibLoadingMore(true);
    try {
      const next = libPagesRef.current + 1;
      const r = await fetchLibPage(next);
      setLibrary(prev => [...prev, ...r.items.filter(v => !prev.find(x => x.id === v.id))]);
      setLibHasMore(r.hasMore);
      if (r.items.length) libPagesRef.current = next;
    } catch { /* keep current list */ }
    setLibLoadingMore(false);
  };

  useEffect(() => { if (tab === 'library' && mode === 'pick') loadLibrary(); }, [tab, mode, loadLibrary]);

  useEffect(() => {
    if (tab !== 'library' || mode !== 'pick') return;
    const converting = library.some(v => v.status && v.status !== 'processed' && v.status !== 'failed');
    if (!converting) return;
    const t = setTimeout(() => loadLibrary(true), 3500);
    return () => clearTimeout(t);
  }, [tab, mode, library, loadLibrary]);

  // ── Pick screen entry points ──
  const openBasePicker = () => { setPickTarget('base'); setPicked(bases); setPickError(''); setTab('upload'); setMode('pick'); };
  const openStitchPicker = () => { setPickTarget('stitch'); setPickError(''); setTab('upload'); setMode('pick'); };

  const togglePick = (v) => {
    setPicked(prev => prev.find(p => p.id === v.id)
      ? prev.filter(p => p.id !== v.id)
      : [...prev, { id: v.id, file_url: v.file_url, title: v.title }]);
  };
  const removePick = (id) => setPicked(prev => prev.filter(p => p.id !== id));

  const confirmBases = () => {
    if (!picked.length) return;
    setBases(picked);
    setActiveBase(0);
    setTitle(prev => prev || picked[0].title || 'Untitled project');
    setMode('edit');
  };

  const pickStitchFromLibrary = (v) => { setStitch({ id: v.id, file_url: v.file_url, title: v.title }); setMode('edit'); };

  // ── Projects ──
  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/getStitchProjects`, { credentials: 'include' }).then(x => x.json());
      setProjects(r.code === 200 && Array.isArray(r.data) ? r.data : []);
    } catch { setProjects([]); }
    setProjectsLoading(false);
  }, []);
  useEffect(() => { if (mode === 'projects') loadProjects(); }, [mode, loadProjects]);

  const clearEditState = () => {
    setBases([]); setActiveBase(0); setStitch(null); setPosition('append');
    setCaptions(defaultCaptions()); setTitle('');
    setResults([]); setExportJobs([]); setExportError(''); setPicked([]); setConvertPct(0);
    setLastExport(null);
  };

  const collectState = () => ({ bases, stitch, position, captions, title, lastExport });

  const newProject = () => {
    projectIdRef.current = null; setProjectId(null);
    clearEditState(); setPickError(''); setPickTarget('base'); setTab('upload'); setMode('pick');
  };

  const openProject = async (id) => {
    try {
      const r = await fetch(`${API}/api/v1/getStitchProject?id=${id}`, { credentials: 'include' }).then(x => x.json());
      if (r.code !== 200 || !r.state) return;
      const s = r.state;
      clearEditState();
      projectIdRef.current = r.id; setProjectId(r.id);
      setTitle(s.title || r.name || '');
      setBases(Array.isArray(s.bases) ? s.bases : []);
      setStitch(s.stitch && s.stitch.id ? s.stitch : null);
      setPosition(s.position === 'prepend' ? 'prepend' : 'append');
      setCaptions({ ...defaultCaptions(), ...(s.captions || {}) });
      setLastExport(Array.isArray(s.lastExport) && s.lastExport.length ? s.lastExport : null);
      setActiveBase(0);
      if (Array.isArray(s.bases) && s.bases.length) setMode('edit');
      else { setPickTarget('base'); setMode('pick'); }
    } catch { /* ignore */ }
  };

  const saveProject = useCallback(async (patch) => {
    try {
      const body = { ...patch };
      if (projectIdRef.current) body.id = projectIdRef.current;
      const r = await fetch(`${API}/api/v1/saveStitchProject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(x => x.json());
      if (r.code === 200 && r.id && !projectIdRef.current) { projectIdRef.current = r.id; setProjectId(r.id); }
      return r.id;
    } catch { return null; }
  }, []);

  const saveProjectName = async (id, name) => {
    try {
      await fetch(`${API}/api/v1/saveStitchProject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name }),
      });
    } catch { /* ignore */ }
  };
  const renameProject = async (id, name) => {
    setProjects(ps => ps.map(p => (p.id === id ? { ...p, name } : p)));
    await saveProjectName(id, name);
  };
  const deleteProject = async (id) => {
    setProjects(ps => ps.filter(p => p.id !== id));
    try {
      await fetch(`${API}/api/v1/deleteStitchProject`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    } catch { /* ignore */ }
  };

  // Create the project on first entry to edit; then autosave (debounced).
  useEffect(() => {
    if (mode !== 'edit' || !bases.length || projectIdRef.current || creatingRef.current) return;
    creatingRef.current = true;
    saveProject({ name: title || 'Untitled project', source_video_id: bases[0].id, state: collectState() })
      .finally(() => { creatingRef.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, bases]);

  useEffect(() => {
    if (mode !== 'edit' || !bases.length || !projectId) return;
    const t = setTimeout(() => {
      saveProject({ name: title || 'Untitled project', source_video_id: bases[0].id, state: collectState() });
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, bases, stitch, position, captions, title, projectId, lastExport]);

  // ── Export: one render-stitched-video job per base clip ──
  const doExport = async () => {
    if (!bases.length) { setExportError('Add at least one base video.'); return; }
    if (!stitch) { setExportError('Choose the video to stitch onto every base clip.'); return; }
    setExportError(''); setResults([]); setExporting(true);

    const jobs = bases.map((b, i) => ({
      sourceId: b.id,
      title: bases.length > 1 ? `${title || 'Stitched'} ${i + 1}` : (title || 'Stitched video'),
      status: 'queued', video: null, jobId: null, error: null,
    }));
    exportJobsRef.current = jobs;
    setExportJobs(jobs);

    for (let i = 0; i < bases.length; i++) {
      const b = bases[i];
      const payload = {
        base_video_id: b.id,
        stitch_video_id: stitch.id,
        output_title: jobs[i].title,
        stitch_position: position,
        captions: captions.enabled ? captions : { enabled: false },
      };
      try {
        const r = await fetch(`${API}/api/v1/render-stitched-video`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).then(x => x.json());
        if (r.code === 200 && r.job_id) { updateExportJob(b.id, { status: 'rendering', jobId: r.job_id }); pollOne(b.id, r.job_id); }
        else updateExportJob(b.id, { status: 'failed', error: r.message || 'Could not start render.' });
      } catch (err) {
        updateExportJob(b.id, { status: 'failed', error: err.message || 'Export failed.' });
      }
    }
    // Remember the batch (persisted via project autosave) so the export can be
    // resumed from another session if this window closes mid-render.
    setLastExport(exportJobsRef.current
      .filter(j => j.jobId)
      .map(j => ({ sourceId: j.sourceId, jobId: j.jobId, title: j.title })));
  };

  // Reopen a previously submitted batch: rebuild the job list and poll each job.
  // Finished jobs resolve on the first poll, so the modal shows current status
  // immediately and flows into the normal results -> schedule screen when done.
  const resumeExport = () => {
    if (!lastExport || !lastExport.length) return;
    const jobs = lastExport.map(b => ({
      sourceId: b.sourceId, title: b.title, status: 'queued', video: null, jobId: b.jobId, error: null,
    }));
    exportJobsRef.current = jobs;
    setExportJobs(jobs);
    setResults([]);
    setExportError('');
    setExporting(true);
    jobs.forEach(j => pollOne(j.sourceId, j.jobId));
  };

  const updateExportJob = (sourceId, patch) => {
    const next = exportJobsRef.current.map(j => (j.sourceId === sourceId ? { ...j, ...patch } : j));
    exportJobsRef.current = next;
    setExportJobs(next);
    if (next.length && next.every(j => j.status === 'done' || j.status === 'failed')) {
      setExporting(false);
      setResults(next.filter(j => j.status === 'done' && j.video).map(j => j.video));
    }
  };

  const pollOne = (sourceId, jobId) => {
    // The node worker renders ONE job at a time, so in a big batch most jobs sit
    // 'queued' for a long while. Time spent waiting in the queue must not count
    // toward the timeout — otherwise late jobs get marked failed while the
    // backend is still happily working through the line.
    let processingTicks = 0; // polls while the job is actively rendering
    let totalTicks = 0;      // safety cap on the whole wait, queue included
    const tick = async () => {
      totalTicks++;
      try {
        const r = await fetch(`${API}/api/v1/render-status?job_id=${jobId}`, { credentials: 'include' }).then(x => x.json());
        if (r.code === 200 && r.status === 'done' && r.video) { updateExportJob(sourceId, { status: 'done', video: r.video }); return; }
        if (r.code === 200 && r.status === 'failed') { updateExportJob(sourceId, { status: 'failed', error: r.error || 'Render failed.' }); return; }
        if (r.code === 404) { updateExportJob(sourceId, { status: 'failed', error: 'Job no longer exists.' }); return; }
        // Paused for transcript review — stop polling; the user edits the text
        // and submitting re-queues the burn (which restarts the poll).
        if (r.code === 200 && r.status === 'awaiting_review') {
          updateExportJob(sourceId, { status: 'review', segments: r.segments || [] });
          return;
        }
        if (r.code === 200 && r.status === 'processing') {
          processingTicks++;
          updateExportJob(sourceId, { status: 'rendering', stage: r.stage || null });
        } else if (r.code === 200 && r.status === 'queued') {
          updateExportJob(sourceId, { status: 'queued' });
        }
      } catch { /* keep polling */ }
      // 172 × 7s ≈ 20 min of ACTIVE rendering per job; ~2h total including queue time.
      if (processingTicks < 172 && totalTicks < 1030) setTimeout(tick, 7000);
      else updateExportJob(sourceId, { status: 'failed', error: 'Render timed out.' });
    };
    tick();
  };

  // ── Scheduling (mirrors the Video Editor) ──
  const SPACING_PRESETS = [
    { label: '1 hour', hours: 1 }, { label: '6 hours', hours: 6 }, { label: '12 hours', hours: 12 },
    { label: '1 day', hours: 24 }, { label: '3 days', hours: 72 }, { label: '5 days', hours: 120 }, { label: '7 days', hours: 168 },
  ];
  const pad2 = (n) => String(n).padStart(2, '0');
  const toInputValue = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const toApiDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
  const scheduleDateFor = (i) => {
    const base = scheduleStart ? new Date(scheduleStart) : new Date();
    return new Date(base.getTime() + (results.length > 1 ? i * spacingHours * 3600 * 1000 : 0));
  };

  const openScheduler = () => {
    const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0);
    setScheduleStart(toInputValue(d));
    setPostText(''); setSelectedAccounts([]); setScheduleError(''); setScheduleDone(null);
    setAccountSearch(''); setAccountTypeFilter('');
    setVariants(Object.fromEntries(results.map(v => [v.id, { title: v.title || '', text: '' }])));
    setShowSchedule(true);
    loadAccounts();
  };

  const patchVariant = (id, patch) =>
    setVariants(prev => ({ ...prev, [id]: { ...(prev[id] || { title: '', text: '' }), ...patch } }));

  const accountTypes = [...new Set(accounts.map(a => a.type))];
  const filteredAccounts = accounts.filter(a => {
    if (accountTypeFilter && a.type !== accountTypeFilter) return false;
    if (!accountSearch) return true;
    const q = accountSearch.toLowerCase();
    return (a.name || '').toLowerCase().includes(q) || (a.type || '').toLowerCase().includes(q);
  });

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const r = await fetch(`${API}/api/v1/getAccounts`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
      }).then(x => x.json());
      setAccounts(r.code === 200 && Array.isArray(r.accounts) ? r.accounts : []);
    } catch { setAccounts([]); }
    setAccountsLoading(false);
  };

  const toggleAccount = (acc) => {
    const key = `${acc.id}_${acc.type}`;
    setSelectedAccounts(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const doSchedule = async () => {
    if (!selectedAccounts.length) { setScheduleError('Select at least one account.'); return; }
    if (!results.length) return;
    setScheduling(true); setScheduleError('');
    const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })();
    let ok = 0, fail = 0;
    for (let i = 0; i < results.length; i++) {
      const v = results[i];
      const when = scheduleDateFor(i);
      // Per-video variation: its own title, and its own caption unless left
      // blank (blank = use the shared caption).
      const variant = variants[v.id] || {};
      const vTitle = (variant.title || v.title || postText || 'Video').slice(0, 100);
      const vText  = (variant.text || '').trim() ? variant.text : postText;
      try {
        const r = await fetch(`${API}/api/v1/schedulePost`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scheduledDate: toApiDate(when),
            text: vText,
            currentScheduledID: 0,
            postStatus: 'publish',
            timezone: tz,
            videoTitle: vTitle,
            images: [],
            videos: [{ id: v.id, file_url: v.file_url, title: vTitle }],
            accounts: selectedAccounts,
            replies: [],
          }),
        }).then(x => x.json());
        if (r.code === 200) ok++; else fail++;
      } catch { fail++; }
    }
    setScheduling(false);
    setScheduleDone({ ok, fail });
  };

  const canExport = bases.length > 0 && !!stitch;

  // Sample caption bar shown on the preview to convey style (real text comes from
  // speech-to-text at render time).
  const sampleCaptionStyle = () => {
    const posStyle =
      captions.position === 'top' ? { top: '6%' } :
      captions.position === 'center' ? { top: '50%', transform: 'translate(-50%, -50%)' } :
      { bottom: '6%' };
    const bg = captions.bgColor ? hexToRgba(captions.bgColor, captions.bgOpacity) : 'transparent';
    // The box replaces the letter outline in the render (ASS BorderStyle=3), so
    // the preview only shows the outline when there's no box.
    const outline = captions.outline && !captions.bgColor
      ? { textShadow: ['-1px -1px 0', '1px -1px 0', '-1px 1px 0', '1px 1px 0', '0 -1px 0', '0 1px 0', '-1px 0 0', '1px 0 0']
          .map(o => `${o} ${captions.outlineColor}`).join(', ') }
      : {};
    return {
      position: 'absolute',
      left: '50%',
      transform: captions.position === 'center' ? 'translate(-50%, -50%)' : 'translateX(-50%)',
      ...posStyle,
      maxWidth: '86%',
      textAlign: 'center',
      fontFamily: fontCss(captions.font),
      fontWeight: 700,
      fontSize: `clamp(11px, ${captions.fontSize * 100}vh, 40px)`,
      color: captions.color,
      background: bg,
      borderRadius: 6,
      padding: captions.bgColor ? '3px 10px' : 0,
      lineHeight: 1.25,
      ...outline,
      pointerEvents: 'none',
    };
  };

  return (
    <MainStructure activeItem="creator-studio">
      <style>{SPIN_CSS}</style>
      <div className="container-fluid p-0">

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => { if (mode === 'projects') navigate('/creator-studio'); else setMode('projects'); }}
              style={ghostBtn} title="Back"
            ><Icon.ArrowLeft size={16} /></button>
            <div>
              <h1 className="h5 mb-0" style={{ fontWeight: 800, color: '#0f172a' }}>Stitched Video Editor</h1>
              <span className="small" style={{ color: '#64748b' }}>
                {mode === 'projects' ? 'Your saved projects' : 'Append one clip onto every video, with optional auto-captions'}
              </span>
            </div>
          </div>
          {mode === 'edit' && (
            <div className="d-flex align-items-center gap-2">
              {lastExport && lastExport.length > 0 && !exporting && exportJobs.length === 0 && (
                <button onClick={resumeExport} style={ghostBtn} title="Reopen the last export batch — check render status and schedule the finished videos">
                  <Icon.RefreshCw size={14} /> Last export ({lastExport.length})
                </button>
              )}
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Video title"
                style={{ border: '1px solid #d1d9e0', borderRadius: 8, padding: '8px 12px', fontSize: '0.85rem', minWidth: 180 }}
              />
              <button onClick={doExport} disabled={exporting || !canExport} style={primaryBtn(exporting || !canExport)}>
                <Icon.Film size={15} /> {bases.length > 1 ? `Stitch & export ${bases.length}` : 'Stitch & export'}
              </button>
            </div>
          )}
        </div>

        {/* ── PROJECTS ── */}
        {mode === 'projects' && (
          <div style={card}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span style={{ fontWeight: 700, color: '#0f172a' }}>Your projects</span>
              <button onClick={newProject} style={primaryBtn(false)}><Icon.Plus size={15} /> New project</button>
            </div>
            {projectsLoading ? (
              <div className="text-center py-5"><Icon.Loader size={22} className="sp-spin" style={{ color: '#94a3b8' }} /></div>
            ) : projects.length === 0 ? (
              <div className="text-center py-5" style={{ color: '#94a3b8' }}>
                <Icon.Film size={34} style={{ marginBottom: 10 }} />
                <div style={{ fontSize: '0.9rem' }}>No projects yet.</div>
                <button onClick={newProject} style={{ ...primaryBtn(false), marginTop: 14 }}><Icon.Plus size={15} /> New project</button>
              </div>
            ) : (
              <div className="row g-3">
                {projects.map(p => (
                  <div key={p.id} className="col-6 col-md-4 col-xl-3">
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                      <div onClick={() => openProject(p.id)} style={{ aspectRatio: '16/10', background: '#0f172a', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                        {p.source_url ? (
                          <video src={`${p.source_url}#t=0.1`} preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon.Film size={26} color="#475569" />
                          </div>
                        )}
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.18)' }}>
                          <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon.Edit2 size={15} color="#0f172a" />
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: '8px 10px' }}>
                        <ProjectName project={p} onSave={renameProject} />
                        <div className="d-flex align-items-center justify-content-between mt-1">
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{fmtDate(p.updated_at)}</span>
                          <Icon.Trash2 size={14} style={{ color: '#ef4444', cursor: 'pointer' }} title="Delete project"
                            onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this project?')) deleteProject(p.id); }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PICK (base multi-select or single stitch clip) ── */}
        {mode === 'pick' && (
          <div style={card}>
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
              <span style={{ fontWeight: 700, color: '#0f172a' }}>
                {pickTarget === 'stitch' ? 'Choose the clip to stitch onto every video' : 'Choose your base videos'}
              </span>
              {bases.length > 0 && (
                <button onClick={() => setMode('edit')} style={ghostBtn}><Icon.ArrowLeft size={14} /> Back to editor</button>
              )}
            </div>
            <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 0 }}>
              {pickTarget === 'stitch'
                ? 'This single clip is appended to (or prepended to) each base video, producing one new video per base.'
                : 'Each of these becomes a separate output: base video + your stitch clip.'}
            </p>

            <div className="d-flex gap-2 mb-3">
              {['upload', 'library'].map(t => (
                <button key={t} onClick={() => setTab(t)} style={tabBtn(tab === t)}>
                  {t === 'upload' ? (pickTarget === 'stitch' ? 'Upload clip' : 'Upload videos') : 'Choose from library'}
                </button>
              ))}
            </div>

            {pickError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{pickError}</div>}

            {tab === 'upload' ? (
              <label
                style={dragOver ? dropzoneActive : dropzone}
                onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
                onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
                onDrop={onDropVideo}
              >
                <input type="file" accept="video/*" multiple={pickTarget === 'base'} hidden disabled={uploading}
                  onChange={e => handleUploadFiles(e.target.files, onUploadReady)} />
                {uploading ? (
                  <div className="text-center">
                    <Icon.Loader size={26} className="mb-2 sp-spin" style={{ color: '#4f46e5' }} />
                    <div style={{ fontSize: '0.9rem', color: '#334155' }}>{uploadPct < 100 ? `Uploading… ${uploadPct}%` : `Converting… ${convertPct || ''}${convertPct ? '%' : ''}`}</div>
                    <div style={{ width: 240, height: 6, background: '#e2e8f0', borderRadius: 4, margin: '10px auto 0' }}>
                      <div style={{ width: `${uploadPct}%`, height: '100%', background: '#4f46e5', borderRadius: 4 }} />
                    </div>
                  </div>
                ) : (
                  <div className="text-center" style={{ pointerEvents: 'none' }}>
                    <Icon.UploadCloud size={34} style={{ color: dragOver ? '#4f46e5' : '#94a3b8', marginBottom: 10 }} />
                    <div style={{ fontWeight: 600, color: '#0f172a' }}>
                      {dragOver ? 'Drop here' : (pickTarget === 'stitch' ? 'Click to upload the stitch clip' : 'Click to upload or drag & drop videos')}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 4 }}>
                      {pickTarget === 'base' ? 'You can select multiple · ' : ''}MP4, MOV, WebM · MOV is auto-converted
                    </div>
                  </div>
                )}
              </label>
            ) : (
              <div>
                {libLoading ? (
                  <div className="text-center py-5"><Icon.Loader size={22} className="sp-spin" style={{ color: '#94a3b8' }} /></div>
                ) : library.length === 0 ? (
                  <div className="text-center py-5" style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No videos in your library yet.</div>
                ) : (
                  <div className="row g-3">
                    {library.map(v => {
                      const isPicked = pickTarget === 'base' ? !!picked.find(p => p.id === v.id) : (stitch && stitch.id === v.id);
                      const ready = (v.status || 'processed') === 'processed';
                      const failed = v.status === 'failed';
                      return (
                        <div key={v.id} className="col-6 col-md-4 col-xl-3">
                          <button
                            onClick={() => { if (!ready) return; if (pickTarget === 'stitch') pickStitchFromLibrary(v); else togglePick(v); }}
                            disabled={!ready}
                            style={{ ...libItem, cursor: ready ? 'pointer' : 'not-allowed', opacity: ready ? 1 : 0.85, borderColor: isPicked ? '#4f46e5' : '#e2e8f0', boxShadow: isPicked ? '0 0 0 2px #c7d2fe' : 'none' }}>
                            <div style={{ position: 'relative' }}>
                              <video src={`${v.file_url}#t=0.1`} preload="metadata" style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8, background: '#000' }} />
                              {ready && (
                                <div style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%', background: isPicked ? '#4f46e5' : 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {isPicked ? <Icon.Check size={14} color="#fff" /> : <Icon.Plus size={14} color="#fff" />}
                                </div>
                              )}
                              <div style={{ position: 'absolute', bottom: 6, left: 6, display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.66rem', fontWeight: 700, color: '#fff', borderRadius: 5, padding: '2px 6px', background: failed ? 'rgba(220,38,38,0.92)' : ready ? 'rgba(22,163,74,0.92)' : 'rgba(79,70,229,0.92)' }}>
                                {ready ? <><Icon.Check size={11} /> Ready</> : failed ? <><Icon.AlertCircle size={11} /> Failed</> : <><Icon.Loader size={11} className="sp-spin" /> Converting…</>}
                              </div>
                              {!ready && !failed && (<div style={{ position: 'absolute', inset: 0, borderRadius: 8, background: 'rgba(15,23,42,0.35)' }} />)}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title || 'Untitled'}</div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!libLoading && libHasMore && (
                  <div className="text-center" style={{ marginTop: 14 }}>
                    <button onClick={loadMoreLibrary} disabled={libLoadingMore} style={ghostBtn}>
                      {libLoadingMore
                        ? <><Icon.Loader size={14} className="sp-spin" /> Loading…</>
                        : <><Icon.ChevronDown size={14} /> Load more videos</>}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Base selection tray */}
            {pickTarget === 'base' && (picked.length > 0 || pickBusy) && (
              <div style={{ marginTop: 16, borderTop: '1px solid #eef2f7', paddingTop: 14 }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>Selected {picked.length > 0 ? `(${picked.length})` : ''}</span>
                  <button onClick={confirmBases} disabled={!picked.length || pickBusy} style={primaryBtn(!picked.length || pickBusy)}>
                    {pickBusy ? 'Processing…' : `Use ${picked.length > 1 ? `${picked.length} base videos` : 'this video'}`}
                  </button>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {picked.map((p, i) => (
                    <div key={p.id} style={{ position: 'relative', width: 84 }}>
                      <video src={`${p.file_url}#t=0.1`} preload="metadata" style={{ width: 84, height: 112, objectFit: 'cover', borderRadius: 8, background: '#000' }} />
                      <span style={{ position: 'absolute', bottom: 4, left: 4, fontSize: '0.66rem', fontWeight: 700, color: '#fff', background: 'rgba(15,23,42,0.7)', borderRadius: 4, padding: '0 5px' }}>{i + 1}</span>
                      <button onClick={() => removePick(p.id)} title="Remove"
                        style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        <Icon.X size={12} />
                      </button>
                    </div>
                  ))}
                  {pickBusy && (
                    <div style={{ width: 84, height: 112, borderRadius: 8, border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon.Loader size={18} className="sp-spin" style={{ color: '#94a3b8' }} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── EDIT ── */}
        {mode === 'edit' && bases.length > 0 && (
          <div className="row g-3">
            {/* Left: stitch layout + preview */}
            <div className="col-12 col-lg-7">
              {/* Base switcher */}
              {bases.length > 1 && (
                <div className="d-flex align-items-center gap-2 mb-2" style={{ overflowX: 'auto', paddingBottom: 4 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>{bases.length} base videos:</span>
                  {bases.map((b, i) => (
                    <button key={b.id} onClick={() => setActiveBase(i)} title={b.title || `Video ${i + 1}`}
                      style={{ position: 'relative', flex: '0 0 auto', width: 52, height: 70, borderRadius: 8, overflow: 'hidden', padding: 0, cursor: 'pointer', background: '#000', border: i === activeBase ? '2px solid #4f46e5' : '1px solid #e2e8f0' }}>
                      <video src={`${b.file_url}#t=0.1`} preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: 'rgba(15,23,42,0.7)', borderRadius: 3, padding: '0 4px' }}>{i + 1}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Preview of the active base with a sample caption bar */}
              <div style={{ background: '#0f172a', borderRadius: 14, padding: 16, display: 'flex', justifyContent: 'center' }}>
                <div style={{ position: 'relative', maxHeight: '62vh', maxWidth: '100%', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
                  {activeBaseVideo && (
                    <video key={activeBaseVideo.id} src={activeBaseVideo.file_url} controls playsInline
                      style={{ maxHeight: '62vh', maxWidth: '100%', display: 'block' }} />
                  )}
                  {captions.enabled && (
                    <div style={sampleCaptionStyle()}>{captions.uppercase ? 'YOUR SPOKEN WORDS APPEAR HERE' : 'Your spoken words appear here'}</div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 6, textAlign: 'center' }}>
                Preview shows the base clip. The stitch clip is added at export; the caption bar is a style sample — the real text comes from the audio.
              </div>

              {/* Stitch layout strip */}
              <div style={{ ...card, marginTop: 12 }}>
                <span style={panelLabel}>Stitch layout</span>
                <div className="d-flex align-items-stretch gap-2 mt-1" style={{ flexWrap: 'wrap' }}>
                  <StitchTile label="Base video" sub={bases.length > 1 ? `× ${bases.length}` : (activeBaseVideo?.title || '')} src={activeBaseVideo?.file_url} dim={position === 'prepend'} order={position === 'prepend' ? 2 : 1} />
                  <div className="d-flex align-items-center" style={{ color: '#94a3b8' }}><Icon.Plus size={18} /></div>
                  {stitch ? (
                    <StitchTile label="Stitch clip" sub={stitch.title || ''} src={stitch.file_url} highlight order={position === 'prepend' ? 1 : 3} />
                  ) : (
                    <button onClick={openStitchPicker} style={{ ...addStitchTile }}>
                      <Icon.PlusCircle size={22} color="#4f46e5" />
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4f46e5', marginTop: 6 }}>Choose stitch clip</span>
                    </button>
                  )}
                </div>

                <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
                  <div className="d-flex gap-1">
                    <button onClick={() => setPosition('append')} style={segBtn(position === 'append')}>Base → Stitch</button>
                    <button onClick={() => setPosition('prepend')} style={segBtn(position === 'prepend')}>Stitch → Base</button>
                  </div>
                  <div className="d-flex gap-2">
                    <button onClick={openBasePicker} style={ghostBtn}>{bases.length > 1 ? 'Edit base videos' : 'Change base'}</button>
                    {stitch && <button onClick={openStitchPicker} style={ghostBtn}>Change stitch clip</button>}
                  </div>
                </div>
              </div>

              {exportError && <div className="alert alert-danger py-2 mt-2" style={{ fontSize: '0.82rem' }}>{exportError}</div>}
            </div>

            {/* Right: caption settings */}
            <div className="col-12 col-lg-5">
              <div style={card}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ ...panelLabel, margin: 0 }}>Auto-captions (speech-to-text)</span>
                  <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: captions.enabled ? '#4f46e5' : '#94a3b8' }}>
                    <input type="checkbox" checked={captions.enabled} onChange={e => setCaptions(c => ({ ...c, enabled: e.target.checked }))} />
                    {captions.enabled ? 'On' : 'Off'}
                  </label>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 0 }}>
                  Transcribes the spoken audio across the whole stitched video and burns the words in as captions.
                </p>

                {captions.enabled ? (
                  <>
                    <Row label="Font">
                      <select value={captions.font} onChange={e => setCaptions(c => ({ ...c, font: e.target.value }))} style={field}>
                        {FONTS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                    </Row>
                    <Row label={`Size ${Math.round(captions.fontSize * 100)}`}>
                      <input type="range" min={3} max={16} value={Math.round(captions.fontSize * 100)}
                        onChange={e => setCaptions(c => ({ ...c, fontSize: Number(e.target.value) / 100 }))} style={{ width: '100%' }} />
                    </Row>
                    <Row label="Position">
                      <div className="d-flex gap-1">
                        {['bottom', 'center', 'top'].map(p => (
                          <button key={p} onClick={() => setCaptions(c => ({ ...c, position: p }))} style={segBtn(captions.position === p)}>{p[0].toUpperCase() + p.slice(1)}</button>
                        ))}
                      </div>
                    </Row>
                    <div className="d-flex gap-3 mb-2">
                      <div className="flex-fill">
                        <label style={smallLabel}>Text color</label>
                        <input type="color" value={captions.color} onChange={e => setCaptions(c => ({ ...c, color: e.target.value }))} style={colorInput} />
                      </div>
                      <div className="flex-fill">
                        <label style={smallLabel}>Box color</label>
                        <input type="color" value={captions.bgColor || '#000000'} onChange={e => setCaptions(c => ({ ...c, bgColor: e.target.value }))} style={colorInput} />
                      </div>
                    </div>
                    <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!captions.bgColor} onChange={e => setCaptions(c => ({ ...c, bgColor: e.target.checked ? (c.bgColor || '#000000') : null }))} />
                      Box behind captions
                    </label>
                    {captions.bgColor && (
                      <Row label={`Box opacity ${Math.round(captions.bgOpacity * 100)}%`}>
                        <input type="range" min={0} max={100} value={Math.round(captions.bgOpacity * 100)}
                          onChange={e => setCaptions(c => ({ ...c, bgOpacity: Number(e.target.value) / 100 }))} style={{ width: '100%' }} />
                      </Row>
                    )}
                    {/* The box replaces the letter outline at render time, so the
                        outline controls only apply when the box is off. */}
                    {!captions.bgColor && (
                      <>
                        <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!captions.outline} onChange={e => setCaptions(c => ({ ...c, outline: e.target.checked }))} />
                          Outline around letters
                        </label>
                        {captions.outline && (
                          <Row label="Outline color">
                            <input type="color" value={captions.outlineColor} onChange={e => setCaptions(c => ({ ...c, outlineColor: e.target.value }))} style={{ ...colorInput, width: 60 }} />
                          </Row>
                        )}
                      </>
                    )}
                    <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!captions.uppercase} onChange={e => setCaptions(c => ({ ...c, uppercase: e.target.checked }))} />
                      UPPERCASE captions
                    </label>
                    <Row label={`Words per line ${captions.maxWords}`}>
                      <input type="range" min={1} max={10} value={captions.maxWords}
                        onChange={e => setCaptions(c => ({ ...c, maxWords: Number(e.target.value) }))} style={{ width: '100%' }} />
                    </Row>

                    <label className="d-flex align-items-start gap-2 mt-2" style={{ fontSize: '0.82rem', color: '#475569', cursor: 'pointer' }}>
                      <input type="checkbox" style={{ marginTop: 3 }} checked={captions.review !== false}
                        onChange={e => setCaptions(c => ({ ...c, review: e.target.checked }))} />
                      <span>
                        <strong style={{ color: '#0f172a' }}>Review transcript before burning</strong>
                        <span style={{ display: 'block', fontSize: '0.74rem', color: '#94a3b8' }}>
                          Each video pauses after transcription so you can fix wrongly heard words (names, brands) before the text goes on the video.
                        </span>
                      </span>
                    </label>
                  </>
                ) : (
                  <div className="text-center py-3" style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                    Captions are off — the stitched videos export without burned-in text.
                  </div>
                )}
              </div>

              {!stitch && (
                <div className="alert alert-warning py-2 mt-3" style={{ fontSize: '0.82rem' }}>
                  Choose a stitch clip to enable export.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Export overlay ── */}
        {(exporting || exportJobs.length > 0) && (
          <div style={modalWrap} onClick={() => {
            // Dismissable once nothing is actively queued/rendering — jobs waiting
            // for transcript review keep running server-side and can be reopened
            // via the "Last export" button.
            const busy = exportJobs.some(j => j.status === 'queued' || j.status === 'rendering');
            if (!busy) { setExporting(false); setExportJobs([]); setResults([]); setExportError(''); }
          }}>
            <div style={modalCard} onClick={e => e.stopPropagation()}>
              {exporting ? (
                <div>
                  <div className="text-center mb-3">
                    <Icon.Loader size={28} className="sp-spin" style={{ color: '#4f46e5', marginBottom: 8 }} />
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                      Stitching {exportJobs.length > 1 ? `${exportJobs.length} videos` : 'your video'}…
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '0.84rem', margin: '4px 0 0' }}>
                      Joining each base clip with the stitch clip{captions.enabled ? ' and transcribing captions' : ''}. This can take a couple of minutes each.
                    </p>
                    {exportJobs.some(j => j.status === 'review') && (
                      <p style={{ color: '#7c3aed', fontSize: '0.82rem', fontWeight: 600, margin: '6px 0 0' }}>
                        <Icon.Edit3 size={13} /> Some videos are waiting for you to review their transcript.
                      </p>
                    )}
                  </div>
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {exportJobs.map((j, i) => (
                      <div key={j.sourceId || i} className="d-flex align-items-center gap-2" style={{ padding: '7px 2px', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                        {j.status === 'done' ? <Icon.CheckCircle size={16} color="#16a34a" />
                          : j.status === 'failed' ? <Icon.XCircle size={16} color="#ef4444" />
                          : j.status === 'review' ? <Icon.Edit3 size={16} color="#7c3aed" />
                          : j.status === 'rendering' ? <Icon.Loader size={16} className="sp-spin" color="#f59e0b" />
                          : <Icon.Clock size={16} color="#94a3b8" />}
                        <span style={{ fontSize: '0.85rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{j.title}</span>
                        {j.status === 'review' ? (
                          <button onClick={() => openReview(j)}
                            style={{ border: '1px solid #ddd6fe', background: '#f5f3ff', color: '#7c3aed', borderRadius: 7, padding: '3px 10px', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Icon.Edit3 size={12} /> Review transcript
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.74rem', fontWeight: j.status === 'rendering' ? 600 : 400, color: j.status === 'failed' ? '#ef4444' : j.status === 'rendering' ? '#b45309' : '#94a3b8' }}>
                            {j.status === 'done' ? 'Done'
                              : j.status === 'failed' ? 'Failed'
                              : j.status === 'rendering' ? `${STAGE_LABELS[j.stage] || 'Rendering'}…`
                              : 'Queued'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-center">
                    <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                      <Icon.Check size={24} color="#16a34a" />
                    </div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>
                      {results.length > 1 ? `${results.length} videos stitched` : 'Saved to your library'}
                    </h3>
                  </div>
                  {results.length === 1 ? (
                    <video src={results[0].file_url} controls style={{ width: '100%', borderRadius: 10, marginTop: 12, maxHeight: 320, background: '#000' }} />
                  ) : (
                    <div className="d-flex gap-2 flex-wrap justify-content-center mt-3">
                      {results.map(v => (
                        <video key={v.id} src={`${v.file_url}#t=0.1`} preload="metadata" style={{ width: 92, height: 124, objectFit: 'cover', borderRadius: 8, background: '#000' }} />
                      ))}
                    </div>
                  )}
                  {exportJobs.some(j => j.status === 'failed') && (
                    <div className="alert alert-warning py-2 mt-3" style={{ fontSize: '0.82rem' }}>
                      {exportJobs.filter(j => j.status === 'failed').length} video(s) failed to render. The rest were saved.
                    </div>
                  )}
                  <div className="d-flex gap-2 justify-content-center mt-3 flex-wrap">
                    {results.length > 0 && (
                      <button onClick={openScheduler} style={primaryBtn(false)}>
                        <Icon.Send size={15} /> {results.length > 1 ? `Schedule ${results.length} videos` : 'Schedule this video'}
                      </button>
                    )}
                    <button onClick={() => navigate('/media-library')} style={ghostBtn}>Open media library</button>
                    <button onClick={() => { setExportJobs([]); setResults([]); }} style={ghostBtn}>Keep editing</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Schedule modal ── */}
        {showSchedule && (
          <div style={modalWrap} onClick={() => { if (!scheduling) setShowSchedule(false); }}>
            {/* Wide two-column layout: captions/variants left, accounts/timing right.
                The success screen stays narrow. */}
            <div style={{ ...modalCard, maxWidth: scheduleDone ? 540 : 1160, width: '96vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  Schedule {results.length > 1 ? `${results.length} videos` : 'video'}
                </h3>
                <button onClick={() => setShowSchedule(false)} style={ghostBtn}><Icon.X size={15} /></button>
              </div>

              {scheduleDone ? (
                <div className="text-center py-2">
                  <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#dcfce7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <Icon.Check size={24} color="#16a34a" />
                  </div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>Scheduled {scheduleDone.ok} post{scheduleDone.ok === 1 ? '' : 's'}</h4>
                  {scheduleDone.fail > 0 && <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{scheduleDone.fail} could not be scheduled.</p>}
                  <p style={{ color: '#64748b', fontSize: '0.85rem' }}>To {selectedAccounts.length} account{selectedAccounts.length === 1 ? '' : 's'}.</p>
                  <div className="d-flex gap-2 justify-content-center mt-3">
                    <button onClick={() => navigate('/schedule-posts')} style={primaryBtn(false)}>View scheduled posts</button>
                    <button onClick={() => setShowSchedule(false)} style={ghostBtn}>Done</button>
                  </div>
                </div>
              ) : (
                <>
                  {scheduleError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{scheduleError}</div>}

                  <div className="row g-4">
                  {/* ── Left: default caption + per-video variations ── */}
                  <div className="col-12 col-lg-7">
                  <label style={smallLabel}>{results.length > 1 ? 'Default caption (used by videos without their own)' : 'Caption (optional)'}</label>
                  <textarea value={postText} onChange={e => setPostText(e.target.value)} rows={2} style={field} placeholder="Write a caption for these posts…" />

                  {/* Per-video variations: each post gets its own title, and
                      optionally its own caption instead of the default. */}
                  <label style={{ ...smallLabel, marginTop: 12 }}>
                    {results.length > 1 ? `Customize each video (${results.length})` : 'Video title'}
                  </label>
                  <div style={{ border: '1px solid #eef2f7', borderRadius: 10, maxHeight: '58vh', overflowY: 'auto' }}>
                    {results.map((v, i) => {
                      const variant = variants[v.id] || { title: v.title || '', text: '' };
                      return (
                        <div key={v.id} className="d-flex gap-2" style={{ padding: 10, borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                          <video src={`${v.file_url}#t=0.1`} preload="metadata"
                            style={{ width: 56, height: 76, objectFit: 'cover', borderRadius: 6, background: '#000', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <input
                              value={variant.title}
                              onChange={e => patchVariant(v.id, { title: e.target.value })}
                              placeholder={`Title for video ${i + 1}`}
                              maxLength={100}
                              style={{ ...field, marginBottom: 6, fontWeight: 600 }}
                            />
                            <textarea
                              value={variant.text}
                              onChange={e => patchVariant(v.id, { text: e.target.value })}
                              rows={2}
                              placeholder={results.length > 1 ? 'Caption for this video (blank = default caption)' : 'Caption override (blank = caption above)'}
                              style={{ ...field, fontSize: '0.8rem' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>

                  {/* ── Right: accounts, timing, spacing, actions ── */}
                  <div className="col-12 col-lg-5">
                  <div className="d-flex align-items-center justify-content-between">
                    <label style={{ ...smallLabel, margin: 0 }}>Post to {results.length > 1 ? '(same accounts for all videos)' : ''}</label>
                    {selectedAccounts.length > 0 && (
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {selectedAccounts.length} selected · <span style={{ color: '#4f46e5', cursor: 'pointer' }} onClick={() => setSelectedAccounts([])}>Clear</span>
                      </span>
                    )}
                  </div>
                  {accountsLoading ? (
                    <div className="text-center py-3"><Icon.Loader size={20} className="sp-spin" style={{ color: '#94a3b8' }} /></div>
                  ) : accounts.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>No connected accounts. <span style={{ color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/social-accounts')}>Connect an account</span></div>
                  ) : (
                    <>
                      <div className="d-flex gap-1 flex-wrap mt-1 mb-2">
                        <button onClick={() => setAccountTypeFilter('')} style={filterChip(accountTypeFilter === '')}>All ({accounts.length})</button>
                        {accountTypes.map(t => (
                          <button key={t} onClick={() => setAccountTypeFilter(t)} style={filterChip(accountTypeFilter === t)}>{t} ({accounts.filter(a => a.type === t).length})</button>
                        ))}
                      </div>
                      <input value={accountSearch} onChange={e => setAccountSearch(e.target.value)} placeholder="Search accounts…" style={{ ...field, marginBottom: 8 }} />
                      <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 8, padding: 8 }}>
                        {filteredAccounts.length === 0 ? (
                          <div style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '8px 4px' }}>No accounts match.</div>
                        ) : filteredAccounts.map(acc => {
                          const key = `${acc.id}_${acc.type}`;
                          const on = selectedAccounts.includes(key);
                          return (
                            <button key={key} onClick={() => toggleAccount(acc)}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px 5px 5px', borderRadius: 20, cursor: 'pointer', fontSize: '0.8rem', border: on ? '1px solid #4f46e5' : '1px solid #e2e8f0', background: on ? '#eef2ff' : '#fff', color: on ? '#4f46e5' : '#475569' }}>
                              {acc.image
                                ? <img src={acc.image} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                                : <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#cbd5e1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#fff' }}>{(acc.name || '?').charAt(0).toUpperCase()}</span>}
                              <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.name || acc.type}</span>
                              <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>{acc.type}</span>
                              {on && <Icon.Check size={13} />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <label style={{ ...smallLabel, marginTop: 12 }}>{results.length > 1 ? 'Start date & time (first video)' : 'Date & time'}</label>
                  <input type="datetime-local" value={scheduleStart} onChange={e => setScheduleStart(e.target.value)} style={field} />

                  {results.length > 1 && (
                    <>
                      <label style={{ ...smallLabel, marginTop: 12 }}>Space the videos</label>
                      <div className="d-flex flex-wrap gap-1">
                        {SPACING_PRESETS.map(p => (
                          <button key={p.hours} onClick={() => setSpacingHours(p.hours)} style={segBtn(spacingHours === p.hours)}>{p.label}</button>
                        ))}
                      </div>
                      <div style={{ marginTop: 10, maxHeight: 150, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 8, padding: '6px 10px' }}>
                        {results.map((v, i) => (
                          <div key={v.id} className="d-flex align-items-center justify-content-between gap-2" style={{ fontSize: '0.78rem', color: '#475569', padding: '2px 0' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {(variants[v.id] && variants[v.id].title) || v.title || `Video ${i + 1}`}
                            </span>
                            <span style={{ color: '#0f172a', fontWeight: 600, flexShrink: 0 }}>{scheduleDateFor(i).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="d-flex gap-2 justify-content-end mt-3">
                    <button onClick={() => setShowSchedule(false)} style={ghostBtn}>Cancel</button>
                    <button onClick={doSchedule} disabled={scheduling || !selectedAccounts.length} style={primaryBtn(scheduling || !selectedAccounts.length)}>
                      {scheduling ? 'Scheduling…' : `Schedule ${results.length > 1 ? `${results.length} posts` : 'post'}`}
                    </button>
                  </div>
                  </div>{/* /col right */}
                  </div>{/* /row */}
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Transcript review modal ── */}
        {reviewJob && (
          <div style={{ ...modalWrap, zIndex: 1070 }} onClick={() => { if (!reviewSaving) setReviewJob(null); }}>
            <div style={{ ...modalCard, maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  <Icon.Edit3 size={16} color="#7c3aed" /> Review transcript
                </h3>
                <button onClick={() => setReviewJob(null)} style={ghostBtn} disabled={reviewSaving}><Icon.X size={15} /></button>
              </div>
              <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 12 }}>
                <strong style={{ color: '#334155' }}>{reviewJob.title}</strong> — fix anything the transcription got wrong
                (site names, brands…). Clear a line to remove that caption. Timings stay as spoken.
              </p>

              {reviewError && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{reviewError}</div>}

              <div style={{ border: '1px solid #eef2f7', borderRadius: 10, maxHeight: '56vh', overflowY: 'auto' }}>
                {reviewJob.segments.length === 0 ? (
                  <div style={{ padding: 14, fontSize: '0.85rem', color: '#94a3b8' }}>No speech was recognised in this video.</div>
                ) : reviewJob.segments.map((s, i) => (
                  <div key={i} className="d-flex gap-2 align-items-center" style={{ padding: '8px 10px', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap', width: 86, flexShrink: 0 }}>
                      {fmtSecs(s.start)} – {fmtSecs(s.end)}
                    </span>
                    <textarea
                      value={s.text}
                      rows={1}
                      onChange={e => setReviewJob(rj => ({
                        ...rj,
                        segments: rj.segments.map((x, xi) => (xi === i ? { ...x, text: e.target.value } : x)),
                      }))}
                      style={{ ...field, fontSize: '0.85rem' }}
                    />
                  </div>
                ))}
              </div>

              <div className="d-flex gap-2 justify-content-end mt-3">
                <button onClick={() => setReviewJob(null)} style={ghostBtn} disabled={reviewSaving}>Review later</button>
                <button onClick={submitTranscript} disabled={reviewSaving} style={primaryBtn(reviewSaving)}>
                  {reviewSaving ? 'Saving…' : <><Icon.Check size={15} /> Looks good — burn captions</>}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </MainStructure>
  );
}

// ── small presentational helpers ──
// 83.4s -> "1:23.4" — compact time labels for the transcript editor.
function fmtSecs(t) {
  const s = Math.max(0, Number(t) || 0);
  const m = Math.floor(s / 60);
  const rest = (s - m * 60).toFixed(1).padStart(4, '0');
  return `${m}:${rest}`;
}
function Row({ label, children }) {
  return (<div className="mb-2"><label style={smallLabel}>{label}</label>{children}</div>);
}

function StitchTile({ label, sub, src, highlight, dim, order }) {
  return (
    <div style={{ order, flex: '0 0 auto', width: 118, border: `1px solid ${highlight ? '#4f46e5' : '#e2e8f0'}`, borderRadius: 10, overflow: 'hidden', background: '#fff', opacity: dim ? 0.9 : 1 }}>
      <div style={{ position: 'relative', height: 150, background: '#000' }}>
        {src ? <video src={`${src}#t=0.1`} preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
             : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.Film size={22} color="#475569" /></div>}
      </div>
      <div style={{ padding: '6px 8px' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: highlight ? '#4f46e5' : '#0f172a' }}>{label}</div>
        <div style={{ fontSize: '0.66rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
      </div>
    </div>
  );
}

function ProjectName({ project, onSave }) {
  const [name, setName] = useState(project.name || '');
  useEffect(() => { setName(project.name || ''); }, [project.name]);
  const commit = () => { const n = name.trim(); if (n && n !== project.name) onSave(project.id, n); };
  return (
    <input
      value={name}
      onChange={e => setName(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      onClick={e => e.stopPropagation()}
      title="Click to rename"
      style={{ width: '100%', border: '1px solid transparent', borderRadius: 6, padding: '2px 4px', fontSize: '0.82rem', fontWeight: 600, color: '#0f172a', background: 'transparent', outline: 'none' }}
      onFocus={e => { e.target.style.border = '1px solid #c7d2fe'; e.target.style.background = '#fff'; }}
      onBlurCapture={e => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
    />
  );
}
function fmtDate(s) {
  if (!s) return '';
  try { return new Date(String(s).replace(' ', 'T')).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return ''; }
}
function hexToRgba(hex, a) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return `rgba(0,0,0,${a})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// ── styles ──
const card = { background: '#fff', border: '1px solid #e8edf2', borderRadius: 14, padding: 20 };
const ghostBtn = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: '#475569', fontSize: '0.82rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 };
const primaryBtn = (disabled) => ({ background: disabled ? '#c7d2fe' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontWeight: 600, fontSize: '0.85rem', cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 });
const tabBtn = (active) => ({ background: active ? '#eef2ff' : '#fff', color: active ? '#4f46e5' : '#64748b', border: `1px solid ${active ? '#c7d2fe' : '#e2e8f0'}`, borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' });
const filterChip = (active) => ({ background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#64748b', border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`, borderRadius: 16, padding: '3px 10px', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer', textTransform: 'capitalize' });
const dropzone = { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, border: '2px dashed #cbd5e1', borderRadius: 12, cursor: 'pointer', background: '#f8fafc', transition: 'border-color .15s, background .15s' };
const dropzoneActive = { ...dropzone, border: '2px dashed #4f46e5', background: '#eef2ff' };
const libItem = { width: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 6, cursor: 'pointer', textAlign: 'left' };
const addStitchTile = { order: 3, flex: '0 0 auto', width: 118, height: 182, border: '1.5px dashed #c7d2fe', borderRadius: 10, background: '#f5f6ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const panelLabel = { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 10, display: 'block' };
const smallLabel = { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', marginBottom: 4 };
const field = { width: '100%', border: '1px solid #d1d9e0', borderRadius: 8, padding: '8px 10px', fontSize: '0.85rem', color: '#0f172a', outline: 'none', boxSizing: 'border-box' };
const colorInput = { width: '100%', height: 34, border: '1px solid #d1d9e0', borderRadius: 8, padding: 2, cursor: 'pointer', background: '#fff' };
const segBtn = (active) => ({ flex: 1, background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#64748b', border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`, borderRadius: 7, padding: '6px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' });
const modalWrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060, padding: 16 };
const modalCard = { background: '#fff', borderRadius: 16, padding: 28, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };

export default StitchedVideoEditor;
